import Api from './api';

/**
 * Serviço para operações de projetos
 * Integração com ProjectController do backend
 */
class ReportService {
  
  /**
   * Buscar todos os projetos com paginação e filtros
   * @param {Object} params - Parâmetros de busca e paginação
   * @returns {Promise} Response com lista paginada de projetos
   */
  static async getAll(params = {}) {
    const requestData = {
      syncId: params.syncId || null,
      projectId: params.projectId || null,
      id: params.id || null,
      sortBy: params.sortBy || 'recent'
    };

    return await Api.post('/reports/getAll', requestData);
  }

  /**
   * Buscar projeto por ID
   * @param {string} id - ID do projeto
   * @returns {Promise} Dados do projeto
   */
  static async getById(id) {
    return await Api.get(`/reports/getById/${id}`);
  }

  /**
   * Criar ou atualizar projeto
   * @param {Object} reportData - Dados do report
   * @returns {Promise} Projeto criado/atualizado
   */
  static async upsert(reportData) {
    return await Api.post('/reports/upsert', reportData);
  }

  /**
   * Deletar projeto
   * @param {string} id - ID do report
   * @returns {Promise} Resultado da operação
   */
  static async delete(id) {
    return await Api.delete(`/reports/${id}`);
  }

  /**
   * Compartilha um relatório e retorna o link de compartilhamento
   * @param {string} reportId - ID do relatório a ser compartilhado
   * @returns {Promise} - Dados do compartilhamento
   */
  static async shareReport(reportId) {
    try {
      
      const response = await Api.post('/reports/share', {
        reportId: reportId
      });
      return response;
    } catch (error) {
      console.error('❌ Erro ao compartilhar relatório:', error);
      throw error;
    }
  }

  /**
   * Busca um relatório compartilhado público (sem autenticação)
   * @param {string} reportId - ID do relatório compartilhado
   * @returns {Promise} - Dados do relatório público
   */
  static async getSharedReport(reportId) {
    try {
      
      // Esta rota deve ser pública (sem Authorization header)
      const response = await Api.get(`/reports/view/${reportId}`, {
        // Sobrescrever headers para remover Authorization se necessário
        headers: {
          'Content-Type': 'application/json'
          // Não incluir Authorization para acesso público
        }
      });
      return response;
    } catch (error) {
      console.error('❌ Erro ao carregar relatório compartilhado:', error);
      throw error;
    }
  }

  /**
   * Atualiza as configurações de permissão do link global
   * @param {string} reportId - ID do relatório
   * @param {Object} permissions - Permissões (dimension, period)
   * @returns {Promise} Resultado da atualização
   */
  static async updateGlobalPermissions(reportId, permissions) {
    try {
      const response = await Api.patch(`/reports/share/global/${reportId}`, permissions);
      return response;
    } catch (error) {
      console.error('❌ Erro ao atualizar permissões globais:', error);
      throw error;
    }
  }

  /**
   * Valida um token de compartilhamento
   * @param {string} token - Token de compartilhamento
   * @returns {Promise} Resultado da validação
   */
  static async validateSharedToken(token) {
    try {
      // Esta rota deve ser pública
      const response = await Api.get(`/reports/shared/validate/${token}`, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      return response;
    } catch (error) {
      console.error('❌ Erro ao validar token de compartilhamento:', error);
      throw error;
    }
  }

  /**
   * Gera o link completo de compartilhamento baseado na URL atual
   * @param {string} reportId - ID do relatório
   * @returns {string} - URL completa de compartilhamento
   */
  static generateFullShareLink(link) {
    const baseUrl = window.location.origin;
    return `${baseUrl}${link}`;
  }

    /**
   * Renova o PIN do relatório compartilhado
   * @param {string} reportId - ID do relatório a ser renovado o PIN
   * @returns {Promise} Novo PIN gerado
   */
  static async renewPinCode(reportId) {
    try {
      const response = await Api.post('/reports/renewPinCode', { reportId });
      return response;
    } catch (error) {
      console.error('❌ Erro ao renovar PIN do relatório:', error);
      throw error;
    }
  }

  /**
   * Valida um PIN para acesso ao relatório compartilhado
   * @param {string} reportId - ID do relatório
   * @param {string} pinCode - PIN digitado
   * @returns {Promise} true se válido, erro caso inválido
   */
  static async validatePinCode(reportId, pinCode) {
    try {
      const response = await Api.post('/reports/validatePinCode', { reportId, pinCode });
      return response;
    } catch (error) {
      console.error('❌ Erro ao validar PIN do relatório:', error);
      throw error;
    }
  }

  /**
   * Cria um novo link de compartilhamento específico
   * @param {Object} data - Dados do link (reportId, title, dimension, period)
   * @returns {Promise} - Link criado
   */
  static async createSharedLink(data) {
    try {
      const response = await Api.post('/reports/share/new', data);
      return response;
    } catch (error) {
      console.error('❌ Erro ao criar link de compartilhamento:', error);
      throw error;
    }
  }

  /**
   * Atualiza um link de compartilhamento existente
   * @param {string} id - ID do link
   * @param {Object} data - Dados para atualizar (title, status, dimension, period)
   * @returns {Promise} - Resultado da atualização
   */
  static async updateSharedLink(id, data) {
    try {
      const response = await Api.patch(`/reports/share/${id}`, data);
      return response;
    } catch (error) {
      console.error('❌ Erro ao atualizar link de compartilhamento:', error);
      throw error;
    }
  }

  /**
   * Remove um link de compartilhamento
   * @param {string} id - ID do link
   * @returns {Promise} - Resultado da remoção
   */
  static async deleteSharedLink(id) {
    try {
      const response = await Api.delete(`/reports/share/${id}`);
      return response;
    } catch (error) {
      console.error('❌ Erro ao remover link de compartilhamento:', error);
      throw error;
    }
  }


  /**
   * Get Schema
   */
  static async getSchema(id) {
    return await Api.get(`/reports/getSchema/${id}`);
  }
}

export default ReportService; 